import { useEffect, useRef, useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modules/shared/ui/components/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/modules/shared/ui/components/command"
import { cn } from "@/lib/utils"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import type {
  GetCandidatesPagedParams,
  VacancyStatus,
  ApplicationStage,
  CandidateSkillsMatchMode,
  CandidateSortField,
  SortDirection,
} from "../../infrastructure/candidate-service"

const ALL_VALUE = "_all"

const VACANCY_STATUS_OPTIONS: { value: VacancyStatus; label: string }[] = [
  { value: "Active", label: "Active" },
  { value: "Published", label: "Published" },
  { value: "Closed", label: "Closed" },
  { value: "Expired", label: "Expired" },
]

const APPLICATION_STAGE_OPTIONS: { value: ApplicationStage; label: string }[] = [
  { value: "Applied", label: "Applied" },
  { value: "Screened", label: "Screened" },
  { value: "Candidate", label: "Candidate" },
  { value: "Hired", label: "Hired" },
  { value: "Rejected", label: "Rejected" },
  { value: "Withdrawn", label: "Withdrawn" },
]

const SORT_BY_OPTIONS: { value: CandidateSortField; label: string }[] = [
  { value: "Name", label: "Name" },
  { value: "Experience", label: "Experience" },
  { value: "AppliedDate", label: "Applied Date" },
]

const SKILLS_MATCH_MODE_OPTIONS: { value: CandidateSkillsMatchMode; label: string }[] = [
  { value: "Any", label: "Any skill" },
  { value: "All", label: "All skills" },
]

interface MultiSelectProps {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  placeholder: string
  searchPlaceholder?: string
  "data-testid"?: string
}

function MultiSelectCombobox({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder = "Search...",
  "data-testid": testId,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? selected[0]
        : `${selected.length} selected`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 justify-between font-normal w-full sm:w-auto sm:min-w-40 sm:max-w-[220px]"
          data-testid={testId}
        >
          <span className="truncate text-sm">{label}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[220px] max-w-[calc(100vw-2rem)]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(opt.value) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface Props {
  filters: GetCandidatesPagedParams
  searchValue: string
  onSearchChange: (value: string) => void
  onFiltersChange: (next: Partial<GetCandidatesPagedParams>) => void
  onClearFilters: () => void
  availableSkills: string[]
  totalCount: number
}

export function CandidateFilterBar({
  filters,
  searchValue,
  onSearchChange,
  onFiltersChange,
  onClearFilters,
  availableSkills,
  totalCount,
}: Props) {
  const { vacancies, loadVacancies } = useVacancies()
  const hasRequestedVacancies = useRef(false)

  useEffect(() => {
    if (!hasRequestedVacancies.current && vacancies.length === 0) {
      hasRequestedVacancies.current = true
      loadVacancies()
    }
  }, [vacancies.length, loadVacancies])

  const vacancyOptions = vacancies.map((v) => ({ value: v.id, label: v.title }))
  const skillOptions = availableSkills.map((s) => ({ value: s, label: s }))

  const selectedVacancyIds = filters.vacancyIds ?? []
  const selectedSkills = filters.skills ?? []

  const hasActiveFilters =
    selectedVacancyIds.length > 0 ||
    !!filters.vacancyStatus ||
    !!filters.applicationStage ||
    selectedSkills.length > 0 ||
    filters.minExperienceYears !== undefined ||
    filters.maxExperienceYears !== undefined ||
    !!filters.location ||
    filters.hasCv !== undefined

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0 basis-full sm:basis-auto sm:min-w-[200px]">
          <Input
            placeholder="Search name, email, phone, location, skills..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="max-w-md"
          />
        </div>
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {totalCount} candidate{totalCount !== 1 ? "s" : ""}
        </span>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <MultiSelectCombobox
          options={vacancyOptions}
          selected={selectedVacancyIds}
          onChange={(ids) =>
            onFiltersChange({ vacancyIds: ids.length > 0 ? ids : undefined })
          }
          placeholder="Vacancies"
          searchPlaceholder="Search vacancies..."
          data-testid="vacancy-ids-filter"
        />

        <Select
          value={filters.vacancyStatus ?? ALL_VALUE}
          onValueChange={(v) =>
            onFiltersChange({
              vacancyStatus: v === ALL_VALUE ? undefined : (v as VacancyStatus),
            })
          }
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Vacancy status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
            {VACANCY_STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.applicationStage ?? ALL_VALUE}
          onValueChange={(v) =>
            onFiltersChange({
              applicationStage: v === ALL_VALUE ? undefined : (v as ApplicationStage),
            })
          }
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All stages</SelectItem>
            {APPLICATION_STAGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {availableSkills.length > 0 && (
          <>
            <MultiSelectCombobox
              options={skillOptions}
              selected={selectedSkills}
              onChange={(skills) =>
                onFiltersChange({ skills: skills.length > 0 ? skills : undefined })
              }
              placeholder="Skills"
              searchPlaceholder="Search skills..."
              data-testid="skills-filter"
            />

            {selectedSkills.length > 1 && (
              <Select
                value={filters.skillsMatchMode ?? "Any"}
                onValueChange={(v) =>
                  onFiltersChange({
                    skillsMatchMode: v as CandidateSkillsMatchMode,
                  })
                }
              >
                <SelectTrigger className="w-full sm:w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SKILLS_MATCH_MODE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}

        <Select
          value={filters.hasCv !== undefined ? String(filters.hasCv) : ALL_VALUE}
          onValueChange={(v) =>
            onFiltersChange({
              hasCv: v === ALL_VALUE ? undefined : v === "true",
            })
          }
        >
          <SelectTrigger className="w-full sm:w-[120px]">
            <SelectValue placeholder="CV" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Any</SelectItem>
            <SelectItem value="true">Has CV</SelectItem>
            <SelectItem value="false">No CV</SelectItem>
          </SelectContent>
        </Select>

        <div className="col-span-2 flex gap-1 items-center sm:col-span-1">
          <Input
            type="number"
            placeholder="Min exp."
            className="w-full sm:w-[90px]"
            value={filters.minExperienceYears ?? ""}
            min={0}
            onChange={(e) =>
              onFiltersChange({
                minExperienceYears: e.target.value !== "" ? Number(e.target.value) : undefined,
              })
            }
          />
          <span className="text-muted-foreground text-xs">–</span>
          <Input
            type="number"
            placeholder="Max exp."
            className="w-full sm:w-[90px]"
            value={filters.maxExperienceYears ?? ""}
            min={0}
            onChange={(e) =>
              onFiltersChange({
                maxExperienceYears: e.target.value !== "" ? Number(e.target.value) : undefined,
              })
            }
          />
          <span className="text-xs text-muted-foreground">yrs</span>
        </div>

        <Input
          placeholder="Location"
          className="col-span-2 w-full sm:col-span-1 sm:w-[140px]"
          value={filters.location ?? ""}
          onChange={(e) => onFiltersChange({ location: e.target.value || undefined })}
        />

        <div className="col-span-2 flex gap-2 sm:col-span-1 sm:ml-auto">
          <Select
            value={filters.sortBy ?? "Name"}
            onValueChange={(v) => onFiltersChange({ sortBy: v as CandidateSortField })}
          >
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_BY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  Sort: {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.sortDirection ?? "Asc"}
            onValueChange={(v) =>
              onFiltersChange({ sortDirection: v as SortDirection })
            }
          >
            <SelectTrigger className="w-full min-w-[90px] sm:w-[90px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Asc">Asc</SelectItem>
              <SelectItem value="Desc">Desc</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedVacancyIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedVacancyIds.map((id) => {
            const label = vacancyOptions.find((v) => v.value === id)?.label ?? id
            return (
              <Badge key={id} variant="secondary" className="gap-1 text-xs">
                {label}
                <button
                  type="button"
                  className="ml-1 rounded-full hover:bg-muted"
                  onClick={() =>
                    onFiltersChange({
                      vacancyIds: selectedVacancyIds.filter((v) => v !== id),
                    })
                  }
                  aria-label={`Remove ${label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      )}
    </div>
  )
}
